import { BigNumber } from "ethers";
import { atom, selector, selectorFamily } from "recoil";

import { accountAtom } from ".";
import { getAccount, listApps } from "../modules/api";
import { appDataAtom } from "../modules/apps";
import { configSelector } from "../modules/config";
import { activeAtom, ampBalanceAtom, fxcBalanceAtom } from "./atoms";

export const contractAddressSelector = selectorFamily({
  key: "contractAddressSelector",
  get:
    (token: string) =>
    ({ get }) => {
      const { contracts } = get(configSelector);
      return contracts[token] ?? "";
    },
});

export const execListApps = async (options) => {
  if (!options?.baseUrl) {
    return null;
  }

  const { data, error } = await listApps(options);
  if (error) {
    throw error;
  }

  return data;
};

export const listAppsQuery = selector({
  key: "listAppsQuery",
  get: async ({ get }) => {
    const { apiUrl } = get(configSelector);

    return await execListApps({ baseUrl: apiUrl });
  },
});

export const listAppsDataAtom = atom({
  key: "listAppsData",
  default: listAppsQuery,
});

export const listAppSelector = selectorFamily({
  key: "listAppSelector",
  get:
    (partition: string) =>
    ({ get }) =>
      (get(listAppsDataAtom).apps || []).find(
        (a) => a.ampPartition === partition
      ),
});

export const execGetAccount = async (account, options) => {
  if (!account || !options?.baseUrl) {
    return null;
  }

  const { data, error } = await getAccount(account, options);
  if (error) {
    throw error;
  }

  return data;
};

export const getAccountQuery = selector({
  key: "getAccountQuery",
  get: async ({ get }) => {
    const account = get(accountAtom);
    const { apiUrl } = get(configSelector);

    return await execGetAccount(account, { baseUrl: apiUrl });
  },
});

export const accountDataAtom = atom({
  key: "accountData",
  default: getAccountQuery,
});

const getAllocationTotalSupply = (alloc) => {
  if (!alloc) {
    return BigNumber.from(0);
  }
  return alloc.supplyComplete
    .add(alloc.supplyPending)
    .add(alloc.unlockComplete)
    .add(alloc.unlockPending);
};

export const ampAllocationsMolecule = selector({
  key: "ampAllocations",
  get: ({ get }) => {
    const apps = get(appDataAtom);
    return get(accountDataAtom)?.allocations?.map((alloc) => {
      const app = apps.find((a) => a.appId === alloc.appId);
      return {
        ...alloc,
        poolType: app?.poolType ?? "app",
        amount: alloc.supplyComplete,
        pending: alloc.supplyPending
          .add(alloc.unlockComplete)
          .add(alloc.unlockPending),
      };
    });
  },
});

export const appAllocationSelector = selectorFamily({
  key: "appAllocation",
  get:
    (appId: string) =>
    ({ get }) => {
      const allocations = get(ampAllocationsMolecule);
      const alloc = allocations?.find((a) => a.appId === appId);
      return alloc;
    },
});

export const ampAllocationsMoleculeByPoolType = selectorFamily({
  key: "ampAllocationsMoleculeByPoolType",
  get:
    (poolType: string) =>
    ({ get }) =>
      get(ampAllocationsMolecule).filter(
        (alloc) => alloc.poolType === poolType
      ),
});

export const ampAllocationsMoleculeByPoolTypeSorted = selectorFamily({
  key: "ampAllocationsMoleculeByPoolTypeSorted",
  get:
    (poolType: string) =>
    ({ get }) =>
      get(ampAllocationsMoleculeByPoolType(poolType))
        ?.slice()
        .sort((a, b) =>
          a?.amount.lt(b?.amount) ? 1 : a?.amount.gt(b?.amount) ? -1 : 0
        ) ?? [],
});

export const pendingReleaseSelector = selectorFamily({
  key: "pendingRelease",
  get:
    (appId: string) =>
    ({ get }) => {
      const alloc = get(appAllocationSelector(appId));
      return (
        alloc?.unlockComplete.add(alloc.unlockPending) ?? BigNumber.from(0)
      );
    },
});

export const totalRewardsSelector = selector({
  key: "totalRewards",
  get: ({ get }) => get(accountDataAtom)?.rewards ?? BigNumber.from(0),
});

export const appWithdrawalProofSelector = selectorFamily({
  key: "appWithdrawalProof",
  get:
    (appId: string) =>
    ({ get }) =>
      get(appAllocationSelector(appId))?.withdrawalProof,
});

export const accountSelector = selector({
  key: "accountSelector",
  get: ({ get }) => {
    const isConnected = get(activeAtom);
    return {
      isConnected,
    };
  },
});

export const suppliedBalanceSelector = selector({
  key: "suppliedBalance",
  get: ({ get }) => {
    const accountData = get(accountDataAtom);
    return (
      accountData?.allocations?.reduce(
        (total, a) => total.add(getAllocationTotalSupply(a)),
        BigNumber.from(0)
      ) ?? BigNumber.from(0)
    );
  },
});

export const appSupplySelector = selectorFamily({
  key: "appSupply",
  get:
    (appId) =>
    ({ get }) => {
      const accountData = get(accountDataAtom);
      const alloc = accountData?.allocations?.find((a) => a.appId === appId);
      return getAllocationTotalSupply(alloc);
    },
});

export const totalAmpSelector = selector({
  key: "totalAmpSelector",
  get: ({ get }) => {
    const supplied = get(suppliedBalanceSelector);
    const ampBalance = get(ampBalanceAtom);
    return supplied?.add(ampBalance);
  },
});

export const accountFxcMolecule = selector({
  key: "accountFxcMolecule",
  get: ({ get }) => get(accountDataAtom)?.legacy,
});

export const totalFxcSelector = selector({
  key: "totalFxc",
  get: ({ get }) => {
    const total =
      get(accountFxcMolecule)?.allocations?.reduce(
        (total, a) => total.add(a.amount),
        BigNumber.from(0)
      ) ?? BigNumber.from(0);

    return total?.add(get(fxcBalanceAtom)) ?? BigNumber.from(0);
  },
});

export const unallocatedFxcSelector = selector({
  key: "unallocatedFxc",
  get: ({ get }) => get(accountFxcMolecule)?.unallocated ?? BigNumber.from(0),
});

export const totalAllocatedFxcSelector = selector({
  key: "totalAllocatedFxc",
  get: ({ get }) =>
    get(accountFxcMolecule)?.allocations?.reduce(
      (total, alloc) => total.add(alloc.amount),
      BigNumber.from(0)
    ) ?? BigNumber.from(0),
});

const fxcTotalRewardSelector = selector({
  key: "fxcTotalReward",
  get: ({ get }) =>
    get(accountFxcMolecule)?.rewards.toString() ?? BigNumber.from(0),
});

export const fxcWithdrawalAuthorizationLimitSelector = selector({
  key: "fxcWithdrawalAuthorizationLimit",
  get: ({ get }) =>
    get(unallocatedFxcSelector)
      .add(get(totalAllocatedFxcSelector))
      .sub(get(fxcTotalRewardSelector)),
});

export const requestedFxcWithdrawalAuthorizationSelector = selector({
  key: "requestedFxcWithdrawalAuthorization",
  get: ({ get }) =>
    get(accountFxcMolecule)?.requestedWithdrawalAuthorization ||
    BigNumber.from(0),
});

export const totalWithdrawableFxcSelector = selector({
  key: "totalWithdrawableFxcSelector",
  get: ({ get }) =>
    get(fxcWithdrawalAuthorizationLimitSelector)
      .add(get(fxcBalanceAtom))
      .add(get(requestedFxcWithdrawalAuthorizationSelector)) ||
    BigNumber.from(0),
});

export const fxcWithdrawalProofSelector = selector({
  key: "fxcAccountWithdrawal",
  get: ({ get }) => get(accountFxcMolecule)?.withdrawalProof,
});
